import React from 'react';
import {
  AgnesBadge,
  AgnesButton,
  AgnesCard,
  AgnesCardContent,
  AgnesDataTable,
  AgnesPageHeader,
  AgnesStatusBadge,
} from '@agnes/ui';
import { KeyRound, Lock, Pencil, Plus, Unlock, UserCog, Users } from 'lucide-react';
import { useUsuariosStore } from '@/stores/useUsuariosStore';
import { usePermissionsStore } from '@/stores/usePermissionsStore';
import { confirm } from '@/stores/useConfirm';
import { EmptyState } from '@/components/ui/EmptyState';
import { SearchInput } from '@/components/SearchInput';
import UsuarioFormModal from '@/components/admin/UsuarioFormModal';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';

type Usuario = any;

const nome = (u: Usuario) => [u.first_name, u.last_name].filter(Boolean).join(' ');
const perfil = (u: Usuario) => u.roles?.[0]?.name ?? '-';

export default function PageUsuarios() {
  const lista = useUsuariosStore((s) => s.lista);
  const busca = useUsuariosStore((s) => s.busca);
  const carregando = useUsuariosStore((s) => s.carregando);
  const [modal, setModal] = React.useState<{ open: boolean; usuario: Usuario | null }>({
    open: false,
    usuario: null,
  });
  const podeEscrever = usePermissionsStore((s) => s.hasPermission('egov:config.usuarios:write'));

  React.useEffect(() => {
    useUsuariosStore.getState().buscar();
  }, []);

  async function onResetarSenha(r: Usuario) {
    const ok = await confirm({
      title: 'Redefinir senha',
      message: `Enviar e-mail de redefinição de senha para <b>${r.email}</b>?`,
      okLabel: 'Enviar',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    await useUsuariosStore.getState().resetarSenha(r.email);
  }

  async function onBloquear(r: Usuario) {
    const ok = await confirm({
      title: r.is_active ? 'Bloquear usuário' : 'Desbloquear usuário',
      message: `Confirmar ${r.is_active ? 'bloqueio' : 'desbloqueio'} de <b>${nome(r)}</b>?`,
      okLabel: r.is_active ? 'Sim, bloquear' : 'Sim, desbloquear',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    await useUsuariosStore.getState().bloquear(r.id, !r.is_active);
  }

  const columns = [
    { key: 'nome', header: 'Nome', width: 200, render: (r: Usuario) => nome(r) },
    { key: 'email', header: 'E-mail', width: 260 },
    {
      key: 'perfil',
      header: 'Perfil',
      width: 180,
      render: (r: Usuario) => <AgnesBadge>{perfil(r)}</AgnesBadge>,
    },
    {
      key: 'situacao',
      header: 'Situação',
      width: 130,
      render: (r: Usuario) => (
        <AgnesStatusBadge
          status={r.is_active ? 'success' : 'error'}
          label={r.is_active ? 'Ativo' : 'Bloqueado'}
          dot
        />
      ),
    },
  ];

  return (
    <>
      <AgnesPageHeader
        icon={<UserCog />}
        title="Usuários"
        subtitle="Gestão de usuários e acessos"
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'Administração' },
          { label: 'Usuários' },
        ]}
        actions={
          podeEscrever ? (
            <AgnesButton
              variant="primary"
              startIcon={<Plus className="h-4 w-4" />}
              onClick={() => setModal({ open: true, usuario: null })}
            >
              Novo usuário
            </AgnesButton>
          ) : undefined
        }
      />

      <AgnesCard>
        <AgnesCardContent className="flex items-stretch">
          <div className="mb-4 max-w-sm">
            <SearchInput
              value={busca}
              onValueChange={(v) => useUsuariosStore.getState().setBusca(v)}
              onSearch={() => useUsuariosStore.getState().buscar()}
              placeholder="Buscar por nome ou e-mail"
            />
          </div>

          {lista.length === 0 ? (
            <EmptyState icon={<Users className="h-7 w-7" />} title="Nenhum usuário para exibir" />
          ) : (
            <TableWrapper>
              <AgnesDataTable<Usuario>
                columns={columns}
                data={lista}
                rowKey={(r) => String(r.id)}
                loading={carregando}
                actions={(r: Usuario) => (
                  <div className="flex items-center justify-center gap-1">
                    <AgnesButton
                      variant="ghost"
                      size="sm"
                      title={podeEscrever ? 'Editar' : 'Ver'}
                      onClick={() => setModal({ open: true, usuario: r })}
                    >
                      <Pencil className="h-4 w-4" />
                    </AgnesButton>
                    {podeEscrever && (
                      <>
                        <AgnesButton
                          variant="ghost"
                          size="sm"
                          title="Redefinir senha"
                          onClick={() => onResetarSenha(r)}
                        >
                          <KeyRound className="h-4 w-4" />
                        </AgnesButton>
                        <AgnesButton
                          variant="ghost"
                          size="sm"
                          title={r.is_active ? 'Bloquear' : 'Desbloquear'}
                          onClick={() => onBloquear(r)}
                        >
                          {r.is_active ? (
                            <Lock className="h-4 w-4" />
                          ) : (
                            <Unlock className="h-4 w-4" />
                          )}
                        </AgnesButton>
                      </>
                    )}
                  </div>
                )}
              />
            </TableWrapper>
          )}
        </AgnesCardContent>
      </AgnesCard>

      <UsuarioFormModal
        open={modal.open}
        usuario={modal.usuario}
        onClose={() => setModal({ open: false, usuario: null })}
      />
    </>
  );
}
